import { i18n } from "@tellescope/i18n";
import { CombinedObservation, useTellescopeMutation } from "@tellescope/tanstack-query";
import { Badge, cn } from "@tellescope/ui";
import {
  formatDate,
  formatTime,
  getMeasurementsWithClassifications,
  getObservationFlags,
  getObservationType,
  type ObservationType,
} from "@tellescope/utils";
import { Activity, Droplets, HeartPulse, LucideIcon, PencilRuler, Ruler, Weight } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { FlagsCell, ReviewButtonCell, ValuesCell } from "./cells";
import { InvalidateButton } from "./InvalidateButton";
import { InvalidationReasonSelection } from "./InvalidationReasonSelection";

const OBSERVATION_TYPE_COLOR_MAP: Record<ObservationType, { bg: string; text: string }> = {
  "blood-pressure": { bg: "bg-red-100", text: "text-red-600" },
  weight: { bg: "bg-blue-100", text: "text-blue-600" },
  height: { bg: "bg-green-100", text: "text-green-600" },
  unknown: { bg: "bg-yellow-100", text: "text-yellow-600" },
  glucose: { bg: "bg-orange-100", text: "text-orange-600" },
  "heart-rate": { bg: "bg-pink-100", text: "text-pink-600" },
};

const getObservationTypeTitles = (): Partial<Record<ObservationType, string>> => ({
  "blood-pressure": i18n.t("Blood Pressure", { ns: "vitals" }),
  weight: i18n.t("Weight", { ns: "vitals" }),
  height: i18n.t("Height", { ns: "vitals" }),
  glucose: i18n.t("Glucose", { ns: "vitals" }),
  "heart-rate": i18n.t("Heart Rate", { ns: "vitals" }),
});

const OBSERVATION_TYPE_ICONS: Record<ObservationType, LucideIcon> = {
  "blood-pressure": Activity,
  weight: Weight,
  height: Ruler,
  glucose: Droplets,
  "heart-rate": HeartPulse,
  unknown: PencilRuler,
};

export interface VitalsSectionItemProps {
  observation: CombinedObservation;
  onClick: (observationId: string, observationType: ObservationType, title: string) => void;
  className?: string;
  timezone?: string;
  selectedThresholdIds?: string[];
  expanded?: boolean;
  canUpdateObservations: boolean;
}

export const VitalsSectionItem = ({
  observation,
  onClick,
  className,
  timezone,
  selectedThresholdIds,
  expanded,
  canUpdateObservations,
}: VitalsSectionItemProps) => {
  const observationType = useMemo(() => getObservationType(observation.measurements), [observation.measurements]);
  const title = useMemo(
    () => getObservationTypeTitles()[observationType] ?? observation.measurements[0]?.unit ?? "",
    [observation.measurements, observationType]
  );
  const flags = useMemo(() => getObservationFlags(observation), [observation]);
  const subtext = useMemo(() => {
    const dateString = formatDate(observation.timestamp, "MMM D", undefined, timezone);
    const timeString = formatTime(observation.timestamp, "hh:mm a", timezone);
    return (
      [(observation.timestampIsEstimated ? "~ " : "") + dateString, timeString, observation.source].join(" · ") +
      (flags.length ? " · " : "")
    );
  }, [flags.length, observation.source, observation.timestamp, observation.timestampIsEstimated, timezone]);

  const iconColors = useMemo(() => OBSERVATION_TYPE_COLOR_MAP[observationType], [observationType]);
  const Icon = OBSERVATION_TYPE_ICONS[observationType];
  const [showInvalidationReasons, setShowInvalidationReasons] = useState(false);

  const observationIds = useMemo(
    () => observation.measurements.map(({ observationId }) => observationId),
    [observation.measurements]
  );

  const updateObservationMutation = useTellescopeMutation({
    model: "enduser_observations",
    method: "updateOne",
  });
  const [isInvalidating, setIsInvalidating] = useState(false);

  const invalidateObservations = useCallback(
    async (reason: string) => {
      setIsInvalidating(true);
      try {
        await Promise.all(
          observationIds.map((id) =>
            updateObservationMutation.mutateAsync({
              id,
              status: "entered-in-error",
              ...(reason ? { invalidationReason: reason } : {}),
            })
          )
        );
        setShowInvalidationReasons(false);
      } finally {
        setIsInvalidating(false);
      }
    },
    [observationIds, updateObservationMutation]
  );

  const showMealFlag = observationType === "glucose";
  const showMedsFlag = observationType === "blood-pressure";
  const showIrregularFlag = observation.measurements.some((observation) => observation.unit === "BPM");

  const showFlagsRow = showMealFlag || showMedsFlag || showIrregularFlag;

  return (
    <div className={cn(className)}>
      <button
        className={cn(
          "border border-gray-100 bg-gray-50 p-2 flex items-center rounded-md hover:bg-gray-100 gap-3 justify-between w-full",
          expanded && "bg-gray-100 border-gray-300 border-2 rounded-b-none"
        )}
        onClick={() => onClick(observation.id, observationType, title)}
      >
        <div className="flex gap-3 items-center">
          <div
            className={cn(
              "rounded-full flex items-center justify-center h-8 w-8 flex-shrink-0",
              iconColors.bg,
              iconColors.text
            )}
          >
            <Icon className="h-4 w-4" />
          </div>
          <div className="flex flex-col gap-1 items-start">
            <p className="font-medium text-sm text-left">{title}</p>
            <div className="flex items-center text-muted-foreground gap-1 text-xs flex-wrap justify-start text-left">
              {subtext}
              {observation.reviewedAt && <div className="h-2 w-2 rounded-full bg-green-400" />}
              {flags.map((flag) => (
                <Badge key={flag} variant="outline" className="px-1.5 py-0 text-[10px]">
                  {flag}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        <ValuesCell
          measurementsWithClassifications={getMeasurementsWithClassifications(observation, selectedThresholdIds)}
          className="flex-shrink-0"
        />
      </button>
      <div
        className={cn(
          "h-2 transition-all border-l border-b border-r border-gray-100 rounded-b-md overflow-hidden space-y-3",
          expanded ? "h-min p-4" : "h-0 p-0"
        )}
      >
        {showFlagsRow && (
          <div className="flex w-full">
            <FlagsCell
              canUpdateObservations={canUpdateObservations}
              observationIds={observationIds}
              showMealFlag={showMealFlag}
              showMedStatusFlag={showMedsFlag}
              showHeartbeatFlag={showIrregularFlag}
              irregularHeartbeat={observation.irregularHeartbeat}
              medStatus={observation.medStatus}
              beforeMeal={observation.beforeMeal}
            />
          </div>
        )}
        <div className="flex w-full">
          {canUpdateObservations && (
            <InvalidateButton observationIds={observationIds} onShowReasons={() => setShowInvalidationReasons(true)} />
          )}
          <div className="flex-grow" />
          <ReviewButtonCell
            isReviewed={!!observation.reviewedAt}
            observationIds={observationIds}
            canUpdateObservations={canUpdateObservations}
          />
        </div>
        {showInvalidationReasons && (
          <InvalidationReasonSelection
            onInvalidate={(reason) => invalidateObservations(reason)}
            onCancel={() => setShowInvalidationReasons(false)}
            invalidating={isInvalidating}
          />
        )}
      </div>
    </div>
  );
};
